import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Alert, Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Formik, Field, ErrorMessage, FormikHelpers, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { FaEnvelope, FaKey, FaArrowLeft, FaGraduationCap, FaShieldAlt } from 'react-icons/fa';
import 'animate.css';
import './Auth.css';

interface ForgotPasswordFormValues {
  email: string;
}

const ForgotPassword: React.FC = () => {
  const { resetPassword, error } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    // تفعيل التحريكات بعد تحميل الصفحة
    setTimeout(() => {
      setAnimate(true);
    }, 100);
  }, []);

  const initialValues: ForgotPasswordFormValues = {
    email: ''
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .email('البريد الإلكتروني غير صالح')
      .required('البريد الإلكتروني مطلوب')
  });

  const handleSubmit = async (
    values: ForgotPasswordFormValues,
    { setSubmitting }: FormikHelpers<ForgotPasswordFormValues>
  ) => {
    try {
      setMessage(null);
      setLoading(true);
      await resetPassword(values.email);
      setMessage('تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني');
    } catch (error) {
      console.error('Password reset error:', error);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page forgot-password-page">
      <div className="auth-background"></div>
      <Container>
        <Row className="justify-content-center align-items-center min-vh-100 py-5">
          <Col md={10} lg={8} xl={6} className="px-3 px-md-4">
            <Card className={`auth-card border-0 shadow-lg ${animate ? 'animate__animated animate__fadeIn' : ''}`}>
              <Card.Header className="text-center py-4 auth-header">
                <div className="logo-container mb-3">
                  <FaGraduationCap size={40} className="logo-icon" />
                </div>
                <h2 className="mb-0 fw-bold">استعادة كلمة المرور</h2>
              </Card.Header>
              <Card.Body className="p-4 p-lg-5">
                <Row>
                  <Col lg={5} className="d-none d-lg-block pe-lg-4">
                    <div className={`text-center mb-5 ${animate ? 'animate__animated animate__fadeInRight animate__delay-1s' : ''}`}>
                      <div className="auth-icon-container mb-4">
                        <FaShieldAlt size={40} className="auth-icon" />
                        <div className="auth-icon-ring"></div>
                      </div>
                      <h4 className="fw-bold mb-3">أمان حسابك</h4>
                      <p className="text-muted mb-4">سنساعدك في استعادة الوصول إلى حسابك بأمان</p>
                    </div>
                    
                    <div className={`auth-features ${animate ? 'animate__animated animate__fadeInUp animate__delay-1s' : ''}`}>
                      <div className="auth-feature mb-3">
                        <FaKey className="text-primary me-2" />
                        <span>إعادة تعيين كلمة المرور بسهولة</span>
                      </div>
                      <div className="auth-feature mb-3">
                        <FaEnvelope className="text-primary me-2" />
                        <span>رابط آمن يرسل إلى بريدك الإلكتروني</span>
                      </div>
                      <div className="auth-feature mb-3">
                        <FaShieldAlt className="text-primary me-2" />
                        <span>حماية متقدمة لحسابك</span>
                      </div>
                    </div>
                  </Col>
                  
                  <Col lg={7} className="ps-lg-4">
                    <div className={`${animate ? 'animate__animated animate__fadeInLeft animate__delay-1s' : ''}`}>
                      {error && <Alert variant="danger" className="animate__animated animate__shakeX">{error}</Alert>}
                      {message && <Alert variant="success" className="animate__animated animate__fadeIn">{message}</Alert>}

                      <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                      >
                        {({ handleSubmit, isSubmitting, touched, errors }) => (
                          <FormikForm onSubmit={handleSubmit}>
                            <div className="mb-4">
                              <Form.Group controlId="email">
                                <Form.Label className="fw-bold">البريد الإلكتروني</Form.Label>
                                <div className={`input-group auth-input-group ${touched.email && errors.email ? 'is-invalid' : ''}`}>
                                  <span className="input-group-text">
                                    <FaEnvelope className="text-primary" />
                                  </span>
                                  <Field
                                    as={Form.Control}
                                    type="email"
                                    name="email"
                                    placeholder="أدخل بريدك الإلكتروني"
                                    className={`py-2 auth-input ${touched.email && errors.email ? 'is-invalid' : ''}`}
                                  />
                                </div>
                                <ErrorMessage
                                  name="email"
                                  component="div"
                                  className="text-danger mt-1 error-message"
                                />
                              </Form.Group>
                            </div>

                            <Button
                              variant="primary"
                              type="submit"
                              className="w-100 py-2 mt-3 mb-3 d-flex align-items-center justify-content-center login-button"
                              disabled={loading || isSubmitting}
                            >
                              {loading ? (
                                <div className="spinner-container">
                                  <div className="spinner"></div>
                                  <span className="ms-2">جاري إرسال الرابط...</span>
                                </div>
                              ) : (
                                <>
                                  <FaKey className="me-2" />
                                  إرسال رابط إعادة التعيين
                                </>
                              )}
                            </Button>
                            
                            <div className="text-center mt-4 pt-3 border-top">
                              <p className="mb-0">
                                <Link to="/login" className="text-primary fw-bold text-decoration-none login-link">
                                  <FaArrowLeft className="me-1" /> العودة إلى تسجيل الدخول
                                </Link>
                              </p>
                            </div>
                          </FormikForm>
                        )}
                      </Formik>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
              <Card.Footer className="bg-light text-center py-3">
                <p className="text-muted mb-0">© {new Date().getFullYear()} منصة السامي التعليمية. جميع الحقوق محفوظة</p>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ForgotPassword;
